"use client";

import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Referrals() {
  const {
    ref: imageRef,
    isVisible: imageVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: contentRef,
    isVisible: contentVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const features = [{
    number: "01",
    title: "Court-Ordered Referrals",
    description: "Structured, neutral documentation for attorneys and judges to support legal proceedings."
  }, {
    number: "02",
    title: "Therapeutic Support",
    description: "Trauma-informed care and clinical support for therapists and families in transition."
  }, {
    number: "03",
    title: "Monitored Exchanges",
    description: "Safe, child-centered transitions for DCS staff and high-conflict cases."
  }, {
    number: "04",
    title: "Professional Reporting",
    description: "Objective, detailed observation logs for GALs, CASA volunteers, and the court."
  }];
  return <section data-section-id="3561" id="legal-clinical-referrals" className="bg-background text-foreground relative overflow-hidden py-20 lg:py-28">
      <div className="mx-auto max-w-[1400px]">
        <div className="grid grid-cols-1 lg:grid-cols-100 gap-y-12 lg:gap-y-0 items-center">
          {}
          <div ref={imageRef} className="lg:col-span-55 relative transition-all duration-1000 ease-out" style={{
          opacity: imageVisible ? 1 : 0,
          transform: imageVisible ? "translateX(0)" : "translateX(-40px)"
        }}>
            <div className="relative lg:-ml-8">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718652/img/legal_professional_consultation.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718652/img/legal_professional_consultation.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718652/img/legal_professional_consultation.png-wvc-sizes" alt="Professional team of supervised visitation specialists, judges, and legal experts collaborating in a neutral office setting" loading="lazy" decoding="async" className="w-full h-[460px] sm:h-[560px] lg:h-[720px] object-cover" />
              {}
              <div className="pointer-events-none absolute -bottom-10 -right-10 w-64 h-64 rounded-lg opacity-60 blur-3xl" style={{
              background: "radial-gradient(circle, hsla(84, 18%, 59%, 0.55) 0%, hsla(84, 13%, 37%, 0.15) 50%, transparent 75%)"
            }} aria-hidden="true" />
            </div>
          </div>

          {}
          <div ref={contentRef} className="lg:col-span-45 px-6 sm:px-10 lg:px-20 transition-all duration-1000 ease-out" style={{
          opacity: contentVisible ? 1 : 0,
          transform: contentVisible ? "translateY(0)" : "translateY(30px)"
        }}>
            {}
            <div className="flex items-center gap-3 mb-8">
              <span className="block w-8 h-px" style={{
              backgroundColor: "var(--secondary)"
            }} aria-hidden="true" />
              <span className="font-mono text-xs tracking-[0.2em] uppercase" style={{
              color: "var(--secondary)"
            }}>
                Legal & Clinical Referrals — 04
              </span>
            </div>

            {}
            <h2 className="font-serif font-default text-4xl sm:text-5xl lg:text-[64px] leading-[1.05] tracking-tight text-foreground mb-8">
              Neutrality and safety
              <br />
              for legal and{" "}
              <em className="font-serif italic font-light">clinical professionals.</em>
            </h2>

            {}
            <div className="space-y-5 mb-12 max-w-xl">
              <p className="font-sans text-base sm:text-lg leading-relaxed text-foreground/80">
                Tiny to Teen Services provides attorneys, judges, and DCS staff with the neutral, factual documentation and rigorous safety protocols required for high-stakes family court matters.
              </p>
              <p className="font-sans text-base sm:text-lg leading-relaxed text-foreground/70">
                Referral partners trust our agency because we bridge the gap between therapeutic needs and legal requirements, supporting GALs, CASA volunteers, and therapists with child-centered, professional oversight.
              </p>
            </div>

            {}
            <div className="grid grid-cols-1 sm:grid-cols-2 mb-12">
              {features.map((feature, i) => <div key={i} data-index={i} className={["py-6 sm:px-6 sm:first:pl-0 border-t border-border", i === 1 ? "sm:border-l" : "", i === 3 ? "sm:border-l" : "", i >= 2 ? "sm:border-t" : "", "first:sm:pl-0"].join(" ")}>
                  <div className="font-mono text-xs tracking-[0.15em] mb-3" style={{
                color: "var(--secondary)"
              }}>
                    → {feature.number}
                  </div>
                  <h3 className="font-sans font-default text-lg leading-tight text-foreground mb-2">
                    {feature.title}
                  </h3>
                  <p className="font-sans text-xs leading-relaxed text-muted-foreground">
                    {feature.description}
                  </p>
                </div>)}
            </div>

            {}
            <Link to="/ContactPage">
              <Button size="lg" className="bg-primary text-white hover:bg-primary/90 font-sans text-xs tracking-[0.2em] uppercase h-12 px-8">
                Submit a Referral
                <ArrowRight className="ml-1 size-4" />
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </section>;
}